import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import MenteePortalNav from './MenteePortalNav';
import MenteeRegistrationChrome from './MenteeRegistrationChrome';
import MenteeRegistrationStepper from './MenteeRegistrationStepper';
import MenteeRegistrationDetails from './MenteeRegistrationDetails';
import MenteeRegistrationLookingFor from './MenteeRegistrationLookingFor';
import MenteeRegistrationFreeText from './MenteeRegistrationFreeText';
import MenteeRegistrationTracks from './MenteeRegistrationTracks';
import MenteeRegistrationActions from './MenteeRegistrationActions';
import MenteePortalService, {
    MenteeLanding,
    MenteeLookingForOption,
    MenteeRegistrationPayload,
    MenteeTrackOption,
} from './MenteePortalService';
import '../mentorDashboard.css';
import './menteeRegistration.css';

const INELIGIBLE_COPY =
    'The mentorship program is not available for your campus';
const MULTI_COHORT_COPY =
    'More than one mentorship cohort is eligible for your account. Please contact your institute administrator before continuing.';
const DEFAULT_AWAITING_NOTE =
    "You're registered for mentorship. A mentor will be paired soon — check back when matching is published.";

const emptyIdentity = {
    picture: null as string | null,
    programName: null as string | null,
    batchYear: null as number | null,
};

const MenteeRegistration = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [ineligible, setIneligible] = useState(false);
    const [multiCohort, setMultiCohort] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [landing, setLanding] = useState<MenteeLanding | null>(null);
    const [awaitingNote, setAwaitingNote] = useState<string | null>(null);
    const [payload, setPayload] = useState<MenteeRegistrationPayload | null>(null);
    const [studentName, setStudentName] = useState<string | null>(null);
    const [linkedinUrl, setLinkedinUrl] = useState('');
    const [lookingFor, setLookingFor] = useState<string[]>([]);
    const [lookingForOptions, setLookingForOptions] = useState<MenteeLookingForOption[]>([]);
    const [freeText, setFreeText] = useState('');
    const [trackIds, setTrackIds] = useState<number[]>([]);
    const [tracks, setTracks] = useState<MenteeTrackOption[]>([]);
    const [savingDraft, setSavingDraft] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [draftHint, setDraftHint] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const applyPayload = (data: MenteeRegistrationPayload) => {
        setPayload(data);
        setLinkedinUrl(data.linkedinUrl ? String(data.linkedinUrl) : '');
        setLookingFor(Array.isArray(data.lookingFor) ? data.lookingFor.map(String) : []);
        setLookingForOptions(Array.isArray(data.lookingForOptions) ? data.lookingForOptions : []);
        setFreeText(data.freeText ? String(data.freeText) : '');
        setTrackIds(Array.isArray(data.trackIds) ? data.trackIds.map(Number) : []);
        setTracks(Array.isArray(data.tracks) ? data.tracks : []);
    };

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            MenteePortalService.getDashboard(),
            MenteePortalService.getRegistration(),
        ]).then(([dashResult, regResult]) => {
            if (cancelled) return;

            if (dashResult.status === 'fulfilled') {
                const dash = dashResult.value;
                setLanding(dash.landing);
                setStudentName(dash.identity?.name ?? null);
                setAwaitingNote(
                    dash.landing === 'registration_awaiting_pairing'
                        ? dash.awaitingPairingNote || DEFAULT_AWAITING_NOTE
                        : null
                );
                setIneligible(false);
                setForbidden(false);
            } else {
                const err = dashResult.reason as { status?: number; message?: string };
                const status = err?.status;
                const message = MenteePortalService.errorMessage(err);
                if (status === 403 && /ineligible/i.test(message)) {
                    setIneligible(true);
                } else if (status === 403 || status === 401) {
                    setForbidden(true);
                }
                setLanding('registration_form');
            }

            if (regResult.status === 'fulfilled') {
                applyPayload(regResult.value);
                setLoadError(null);
                setMultiCohort(false);
                if (dashResult.status !== 'fulfilled') {
                    setIneligible(false);
                    setForbidden(false);
                }
            } else {
                const err = regResult.reason as { status?: number; message?: string };
                const status = err?.status;
                const message = MenteePortalService.errorMessage(err);
                if (status === 409 && /multiple eligible cohorts/i.test(message)) {
                    setMultiCohort(true);
                    setLoadError(null);
                } else if (status === 403 && /ineligible/i.test(message)) {
                    // Admin-Controlled paired mentees: form ineligible; portal landing still applies.
                    if (dashResult.status !== 'fulfilled') {
                        setIneligible(true);
                    }
                    setLoadError(null);
                } else if (status === 403 || status === 401) {
                    if (dashResult.status !== 'fulfilled') {
                        setForbidden(true);
                    }
                    setLoadError(null);
                } else {
                    setLoadError(message);
                }
            }
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!loading && landing === 'dashboard') {
        return <Navigate to="/alumni/mentorship/mentee/dashboard" replace />;
    }

    const readOnly = Boolean(payload?.immutable || payload?.status === 'SUBMITTED');
    const showAwaiting = landing === 'registration_awaiting_pairing';

    const writeBody = () => ({
        lookingFor,
        trackIds,
        freeText: freeText.trim() ? freeText.trim() : null,
        linkedinUrl: linkedinUrl.trim() ? linkedinUrl.trim() : null,
    });

    const onSaveDraft = () => {
        setSavingDraft(true);
        setDraftHint(null);
        setActionError(null);
        MenteePortalService.saveDraft(writeBody())
            .then((data) => {
                applyPayload(data);
                setDraftHint('Draft saved.');
            })
            .catch((err: unknown) => {
                setActionError(MenteePortalService.errorMessage(err));
            })
            .finally(() => setSavingDraft(false));
    };

    const onSubmit = () => {
        setActionError(null);
        setDraftHint(null);
        if (lookingFor.length < 1 || trackIds.length < 1) {
            setActionError(
                lookingFor.length < 1 && trackIds.length < 1
                    ? 'Select at least one looking-for choice and one career track before submitting.'
                    : lookingFor.length < 1
                      ? 'Select at least one looking-for choice before submitting.'
                      : 'Select at least one career track before submitting.'
            );
            return;
        }
        setSubmitting(true);
        MenteePortalService.submit(writeBody())
            .then((data) => {
                applyPayload(data);
            })
            .catch((err: unknown) => {
                setActionError(MenteePortalService.errorMessage(err));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <div className="container py-4 mdash mreg">
            <MenteeRegistrationChrome />
            <MenteePortalNav landing={landing} />
            <div className="mreg-page-head">
                <h1 className="mreg-title">Mentorship Registration</h1>
                <p className="mreg-sub">
                    Express your interest to be matched with an alumni mentor · Select up to 3 career tracks
                </p>
            </div>
            <MenteeRegistrationStepper />

            {loading && <p className="mdash-empty">Loading…</p>}
            {!loading && ineligible ? (
                <p className="mdash-empty mreg-state" role="status">
                    {INELIGIBLE_COPY}
                </p>
            ) : null}
            {!loading && multiCohort ? (
                <p className="mdash-empty mreg-state" role="status">
                    {MULTI_COHORT_COPY}
                </p>
            ) : null}
            {!loading && forbidden ? (
                <p className="mdash-empty mreg-state" role="status">
                    You do not have access to mentee registration.
                </p>
            ) : null}
            {!loading && loadError ? <p className="mdash-empty">{loadError}</p> : null}

            {!loading && showAwaiting ? (
                <div className="mreg-banner" role="status">
                    <strong>Registered — awaiting mentor pairing</strong>
                    <p className="mreg-banner-note">{awaitingNote || DEFAULT_AWAITING_NOTE}</p>
                </div>
            ) : null}

            {!loading && !ineligible && !multiCohort && !forbidden && !loadError && payload ? (
                <>
                    {readOnly ? (
                        <div className="mreg-banner" role="status">
                            <strong>Registration submitted</strong>
                            {payload.submittedAt ? (
                                <span> · {payload.submittedAt}</span>
                            ) : null}
                            <p className="mreg-banner-note">This form is locked and cannot be edited.</p>
                        </div>
                    ) : null}

                    <div className="mreg-layout">
                        <div className="mreg-col mreg-col-left">
                            <MenteeRegistrationDetails
                                identity={payload.identity || emptyIdentity}
                                studentName={studentName}
                                linkedinUrl={linkedinUrl}
                                readOnly={readOnly}
                                onLinkedinChange={setLinkedinUrl}
                            />
                            <MenteeRegistrationLookingFor
                                options={lookingForOptions}
                                selected={lookingFor}
                                readOnly={readOnly}
                                onChange={setLookingFor}
                            />
                            <section className="mreg-card mreg-card-freetext">
                                <MenteeRegistrationFreeText
                                    value={freeText}
                                    readOnly={readOnly}
                                    onChange={setFreeText}
                                />
                            </section>
                        </div>
                        <div className="mreg-col mreg-col-right">
                            <MenteeRegistrationTracks
                                tracks={tracks}
                                selected={trackIds}
                                readOnly={readOnly}
                                onChange={setTrackIds}
                            />
                        </div>
                    </div>
                    <MenteeRegistrationActions
                        readOnly={readOnly}
                        savingDraft={savingDraft}
                        submitting={submitting}
                        draftHint={draftHint}
                        submitHint={actionError}
                        onSaveDraft={onSaveDraft}
                        onSubmit={onSubmit}
                    />
                </>
            ) : null}

            {!loading &&
            showAwaiting &&
            !payload &&
            !ineligible &&
            !multiCohort &&
            !forbidden &&
            !loadError ? (
                <p className="mreg-sub">
                    Your registration is complete. Mentorship registration form is not required for your
                    campus mode.
                </p>
            ) : null}
        </div>
    );
};

export default MenteeRegistration;
